/**
 * What people have done with a document, and what this reader is doing with it now.
 *
 * Three facts about a document live here, and they are one concern: who has played it
 * (`playsQuery`, the board, the strip's proof), where this reader got to in it *before*
 * this visit (their own repo's place, and the last play they submitted), and what they
 * have answered *during* it (the session ref, its one render, and the submission a
 * finished page makes). They are one concern because they answer each other: a card
 * offers "carry on from page 12" out of the place and the plays together, a submission
 * carries the answers of earlier sittings back up (`adoptEarlierAnswers`), and a finished
 * page writes the place as well as the score. Split across two hooks, `resume` — which
 * reads this visit's own page and is read by this visit's own take-up — would be a cycle.
 *
 * It is a hook rather than part of `PdfCarousel` because a play is a record written into
 * somebody's repo, on a clock of its own (the place waits three seconds; the score goes up
 * on the answer), and that machinery should be readable without the stage's JSX around it
 * (`IMPROVEMENT_BACKLOG.md` §3, the rest of the two surfaces' split).
 *
 * What it does not own: the document — the material, the page table and where the first
 * question is all arrive from `./useStageMaterial`, and the page the card is showing
 * arrives from the card.
 */
import {useCallback, useEffect, useRef, useState} from 'react'

import {
  type DocumentMaterial,
  type DocumentMaterialRef,
} from '#/lib/document/material'
import {
  type DocumentPlace,
  documentPlaceFromRead,
} from '#/lib/document/progress'
import {
  adoptEarlierAnswers,
  buildDocumentLeaderboard,
  type DocumentPlaySession,
  documentResume,
  EMPTY_PLAY_SESSION,
  mergePageIntoSession,
  mergeSeenPageIntoSession,
  pagesReadForSubmission,
  selectPlayerProof,
} from '#/lib/document/report'
import {
  useCreateDocumentPlayMutation,
  useDocumentPlaysQuery,
} from '#/state/queries/document-plays'
import {
  useDocumentPlaceQuery,
  useSaveDocumentPlace,
} from '#/state/queries/document-progress'
import {useDialogControl} from '#/components/Dialog'
import {type FlashBookPageResult} from '#/types/flashbook'

/**
 * How long a page turn waits before the reader's place is offered to their repo.
 *
 * Three seconds, which is the FlashBook's interval for the same job and for the same
 * reason: a page turn is worth writing to the machine under the reader's hands and is
 * not worth a request. Long enough that turning quickly through a document is one
 * write, short enough that a reader who closes it and picks up their phone finds the
 * page they stopped on rather than the one before it.
 */
const PLACE_WRITE_DELAY_MS = 3000

export function useDocumentPlaySession({
  postUri,
  embedTitle,
  documentPageCount,
  publishedPageCount,
  firstQuizPage,
  viewerDid,
  isComposing,
  isPreview,
  isNear,
  material,
  pageTable,
}: {
  /** The post this document belongs to, once it has one. */
  postUri?: string
  /**
   * The document's own name, which a play and a place are written with.
   *
   * Optional, as it is on the post: a document posted without a title is played and
   * placed under no name rather than under an empty one.
   */
  embedTitle?: string
  /**
   * The document's length in pages, as the surface that parsed the file knows it.
   *
   * What the board measures a read's progress against, and it is taken from the *file*
   * when the file has been parsed — that is the truth about this deck — with the post's
   * own count behind it, so a card whose PDF has not loaded (a document scrolled out of
   * view still says who has played it) ranks its readers against the length the author
   * published rather than against nothing. Nought pages is not a length, so it arrives
   * as `undefined` instead of being handed on as one.
   */
  documentPageCount?: number
  /** The length the *post* claims, for the resume's idea of reaching the end. */
  publishedPageCount?: number
  /** The first page that asks anything: `undefined` means nothing to play. */
  firstQuizPage?: number
  /** This reader's DID, when somebody is signed in: without one nothing is recorded. */
  viewerDid?: string
  /** Whether the card is the author writing the document. */
  isComposing: boolean
  /** Composer preview: questions play here, and nothing is recorded. */
  isPreview: boolean
  /** Whether the card is on screen — asked before either query runs. */
  isNear: boolean
  /** The questions themselves, when the material has arrived. */
  material?: DocumentMaterial
  /** What each page holds, from the post or from the composer's own state. */
  pageTable?: DocumentMaterialRef
}) {
  /** What this reader has answered in this visit — the play that gets recorded. */
  const session = useRef<DocumentPlaySession>(EMPTY_PLAY_SESSION)
  const playsControl = useDialogControl()
  /**
   * Set the moment the author asks for the summary, and never unset.
   *
   * The dialog is mounted with the card, so it cannot ask the control whether it
   * is open — it has to be told that somebody wanted it. Once wanted, the answer
   * is cached, so asking again costs nothing.
   */
  const [playsWanted, setPlaysWanted] = useState(false)
  /** The one press behind both doors into the plays: the page's control, the strip. */
  const openPlays = useCallback(() => {
    setPlaysWanted(true)
    playsControl.open()
  }, [playsControl])
  const createPlay = useCreateDocumentPlayMutation()
  const canRecordPlay = !!postUri && !!viewerDid && !isPreview && !isComposing

  /*
   * Who has played the document, asked for by the card itself.
   *
   * A published document that asks something is a thing other people are working
   * through, and saying so is the card's best argument for being started — so this
   * is not the author's request. It is gated the way the page's own fetch is:
   * signed in, actually on screen, and a document with something to play. A column
   * of cards nobody has scrolled to asks nothing, and neither does a reader who is
   * signed out.
   */
  const playsQuery = useDocumentPlaysQuery(postUri, {
    enabled:
      !!postUri &&
      !!viewerDid &&
      !isComposing &&
      !isPreview &&
      isNear &&
      firstQuizPage !== undefined,
  })
  const leaderboard = buildDocumentLeaderboard({
    plays: playsQuery.data ?? [],
    material,
    pageCount: documentPageCount,
    /*
     * The page table as well as the material, because the board is ranked on
     * progress and the material only says how big the document is once the
     * questions have been fetched — which, on a card nobody has played yet, they
     * have not. The table is on the post, so the crown and the dialog's #1 are
     * decided by the same numbers either way.
     */
    pageTable,
    viewerDid,
  })
  /** What the strip draws, or `undefined` when nobody has played it yet. */
  const playerProof = selectPlayerProof({leaderboard})

  /**
   * What this reader has answered *on this card*, once they have answered anything.
   *
   * The session itself is a ref, because a read in progress is accumulated without
   * anybody watching it; this is the one moment it is worth a render, which is the
   * moment its contents decide what the card offers next.
   */
  const [read, setRead] = useState<DocumentPlaySession | undefined>(undefined)

  /**
   * Where this reader got to in this document, from their own repo.
   *
   * The half of a read that no submission can hold: a play is the pages that asked
   * something and were answered, and a document is mostly pages that ask nothing. So
   * this is what makes "carry on from page 12" possible for a reader whose last
   * answer was on page 4 — see `#/lib/document/progress`. Asked on the card's own
   * terms (signed in, on screen), because it is the card that needs it.
   */
  const placeQuery = useDocumentPlaceQuery(postUri, {
    enabled: canRecordPlay && isNear,
  })
  /*
   * The mutation's `mutate` alone, and deliberately: `useMutation`'s returned object
   * is rebuilt on every render, so a callback that closed over it would change on
   * every render — and the effect that flushes the place on the way out would flush
   * on *every* render instead of on the way out, each flush being another render.
   */
  const {mutate: savePlace} = useSaveDocumentPlace()
  const place = placeQuery.data ?? undefined

  /**
   * Where this reader's own read stopped, when it did not finish.
   *
   * The memory is not the card's: every page a reader finishes goes up as their
   * whole read (`#/state/queries/document-plays`), and `documentResume` reads that
   * submission back against the post's page table. So a read left half-done survives
   * a reload and a change of device with nothing stored here — the read in progress
   * on *this* card is offered ahead of it, because it is the newest thing the reader
   * did and the server has not been told about it yet — and the pages the reader has
   * *read* are in their own repo (`#/lib/document/progress`), because no submission
   * carries them.
   */ const resume = documentResume({
    plays: playsQuery.data ?? [],
    viewerDid,
    pageTable,
    read,
    place,
    /*
     * The document's length, so "finished" can mean *read to the end* and not
     * just "out of questions": a reader at page 12 of 28 with every question
     * behind them has sixteen pages left, and the offer to carry on is the
     * only thing that would take them there.
     */
    pageCount: publishedPageCount || undefined,
  })

  /**
   * The place waiting to be written, and the clock that is waiting with it.
   *
   * A page turn is worth a device write and is not worth a request, so the place is
   * gathered and offered to the repo on a slower clock than the page (see the
   * FlashBook's place, which makes the same trade at the same interval). The ref is
   * what gets written, not the render's snapshot: a flush that fired between two
   * page turns would otherwise write a stale path.
   */
  const pendingPlace = useRef<DocumentPlace | null>(null)
  const placeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flushPlace = useCallback(() => {
    if (placeTimer.current !== null) {
      clearTimeout(placeTimer.current)
      placeTimer.current = null
    }
    const place = pendingPlace.current
    // Nothing read on this card means nothing to write — and never a write that
    // clears: a card that merely unmounted has no business erasing a place.
    if (!postUri || !place) return
    savePlace({
      documentUri: postUri,
      documentTitle: embedTitle,
      place,
    })
  }, [embedTitle, postUri, savePlace])
  /*
   * `setTimeout` rather than a transition: the place is not a render concern, and a
   * write that lands after the reader has closed is a write that landed.
   */
  useEffect(() => {
    return () => flushPlace()
  }, [flushPlace])

  /**
   * Take up the answers a previous sitting left, once, on the way in.
   *
   * A sitting in this visit and a submission from an earlier one are one read, and
   * the sitting starts empty — so the *first* thing either surface does (turning a
   * page, answering one) adopts what the server already knows. What it does not adopt
   * is the submission's idea of the *path*: the pages answered are all a submission
   * can prove, and this sitting's own path — including the pages that ask nothing —
   * is the newer truth about where the reader is.
   *
   * The rule itself — including what is *not* worth adopting — lives in the pure
   * module and is tested there (`adoptEarlierAnswers`); what is here is the ref it
   * writes and the answer the doors need. A door that is about to render the read
   * wants to know whether *this call* put anything in it, and the answer is `null`
   * when it did not: the session a reader already has is not news, and a report
   * that treats it as news renders forever (`reportReaderPage`).
   */
  const takeUpEarlierAnswers = useCallback((): DocumentPlaySession | null => {
    const adopted = adoptEarlierAnswers(session.current, resume, material)
    if (!adopted) return null
    session.current = adopted
    return adopted
  }, [material, resume])

  /**
   * A page came into view *in the reader*.
   *
   * Only the full screen reader reports, and that is a decision rather than an
   * oversight: opening it is a reader saying they mean to read, where a card is one
   * of a column the feed renders whether or not anybody looks at it — a place
   * written on render would have every document in a feed claiming to have been
   * stopped at page 1.
   *
   * The path is a property of the read, like the answers are: this folds into the
   * same session the answers do, so a read that is started on the stage and continued
   * in the reader is one read either way.
   *
   * `setRead` is listed among its dependencies below, which looks like noise and is not:
   * React Compiler infers the *setter* as a dependency of a callback that calls it, and a
   * hand-written memo whose list does not match what it inferred is a memo it refuses to
   * preserve — which costs the whole card its compilation. A setter is stable, so listing
   * it changes nothing at runtime; leaving it out changes whether this file compiles at
   * all (`#/lib/reactCompilerMemoization`). The same goes for `recordQuizResult` below.
   */
  const reportReaderPage = useCallback(
    (page: number) => {
      if (isComposing || isPreview || !postUri) return
      const adopted = takeUpEarlierAnswers()
      const merged = mergeSeenPageIntoSession(session.current, page)
      /*
       * A report that changes nothing renders nothing.
       *
       * This callback is a prop of the reader, and it is rebuilt whenever the read it
       * is built from changes — which is every write it makes. The reader is deaf to
       * that on its own side (`usePageReport` fires on the page and nothing else), so
       * a rebuilt function is no longer a re-report; what is left is a report that
       * genuinely says nothing new, which is a reader reopened at the page the read
       * already ends on (`mergeSeenPageIntoSession` hands back the same session).
       * Answering it anyway is a `setState` with a fresh object plus a scheduled place
       * write, so "nothing changed" has to mean "nothing written".
       */
      if (!adopted && merged === session.current) return
      session.current = merged
      setRead(session.current)
      const next = documentPlaceFromRead(session.current)
      if (!next) return
      pendingPlace.current = next
      if (placeTimer.current !== null) clearTimeout(placeTimer.current)
      placeTimer.current = setTimeout(flushPlace, PLACE_WRITE_DELAY_MS)
    },
    [
      flushPlace,
      isComposing,
      isPreview,
      postUri,
      setRead,
      takeUpEarlierAnswers,
    ],
  )

  /**
   * A page's questions finished — here, or in the full screen reader.
   *
   * The play is what this reader has answered so far, not just this page: a page
   * answered twice replaces its own earlier answers, and what goes up is the whole
   * set, so the author's report can never count a learner's correction as a second
   * attempt. Both surfaces finish into this one function, so a read that starts on
   * the stage and continues in full screen is one read either way.
   *
   * The read this merges into begins where the reader left off rather than at
   * nothing. A reader who carries on has already answered pages the server knows
   * about, and a submission carrying only the new page would read as those answers
   * having been taken back — the author's report would lose a page every time a
   * learner came back for another one (`documentResume`).
   */
  const recordQuizResult = useCallback(
    (result: {
      pageResults: FlashBookPageResult
      score: number
      maxPoints: number
    }) => {
      if (isComposing || isPreview) return
      // The questions are in hand by now — the reader has just answered one — so the
      // per-page maxima a submission does not carry are read off them inside, which
      // is what keeps a re-answered page from inflating its maximum.
      takeUpEarlierAnswers()
      session.current = mergePageIntoSession(session.current, result)
      setRead(session.current)
      /*
       * The place carries the *answers* as well as the page, so answering has to
       * refresh it: a card that said "carry on from page 12" beside a count of two
       * answered when three had been answered would be a card that had forgotten
       * something the reader had just done.
       */
      if (postUri) {
        const next = documentPlaceFromRead(session.current)
        if (next) {
          pendingPlace.current = next
          if (placeTimer.current !== null) clearTimeout(placeTimer.current)
          placeTimer.current = setTimeout(flushPlace, PLACE_WRITE_DELAY_MS)
        }
      }
      if (!canRecordPlay || !postUri) return
      createPlay.mutate({
        documentUri: postUri,
        title: embedTitle,
        score: session.current.score,
        maxPoints: session.current.maxPoints,
        timeMs: session.current.timeMs,
        pageResults: session.current.results,
        /*
         * How far the read has got, beside what it answered. The sitting's own path
         * is the newest word on where the reader is, and the record in their repo is
         * what no sitting can hold — the pages of the visits before this one — so the
         * two are unioned (`pagesReadForSubmission`) and the author's report can say
         * "twelve of sixteen pages" about a read whose last answer was on page 4.
         */
        pagesSeen: pagesReadForSubmission(session.current, place),
      })
    },
    [
      canRecordPlay,
      createPlay,
      flushPlace,
      embedTitle,
      isComposing,
      isPreview,
      place,
      postUri,
      setRead,
      takeUpEarlierAnswers,
    ],
  )

  return {
    /** Who has played it, ranked — what the card's own summary draws. */
    leaderboard,
    /** What the strip under the page draws, or `undefined` when nobody has played it. */
    playerProof,
    /** Where this reader has got to: this visit's answers, and the play before it. */
    resume,
    /** The author's summary dialog, and whether anybody has asked for it yet. */
    playsControl,
    playsWanted,
    openPlays,
    /** A page came into view in the reader. */
    reportReaderPage,
    /** A page's questions finished, here or in the full screen reader. */
    recordQuizResult,
    /** Write the place now rather than waiting out the clock (the reader is closing). */
    flushPlace,
  }
}
